/** Tenant colors are presentation only. Never accept CSS expressions or load
 * fonts from configuration. Keep status/trust colors independent of branding. */
type RGB = [number, number, number];

export function parseBrandColor(value: unknown): RGB | null {
  if (typeof value !== 'string' || !/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim())) return null;
  let hex = value.trim().slice(1);
  if (hex.length === 3) hex = Array.from(hex).map(c => c + c).join('');
  return [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16)) as RGB;
}

export function colorContrast(a: RGB, b: RGB): number {
  const luminance = (rgb: RGB) => rgb.map(n => {
    const s = n / 255;
    return s <= .04045 ? s / 12.92 : ((s + .055) / 1.055) ** 2.4;
  }).reduce((sum, n, i) => sum + n * [.2126, .7152, .0722][i], 0);
  const x = luminance(a), y = luminance(b);
  return (Math.max(x, y) + .05) / (Math.min(x, y) + .05);
}

// Adjust toward black/white only as far as needed. This is bounded work once
// per tenant, not a per-component runtime color calculation.
function accessibleColor(rgb: RGB, background: RGB, toward: 0 | 255, ratio: number, opacity = 1, backgroundTint = 0): RGB {
  for (let step = 0; step <= 100; step++) {
    const candidate = rgb.map(n => Math.round(n + (toward - n) * step / 100)) as RGB;
    const composite = candidate.map((n, i) => n * opacity + background[i] * (1 - opacity)) as RGB;
    const backdrop = background.map((n, i) => n * (1 - backgroundTint) + candidate[i] * backgroundTint) as RGB;
    if (colorContrast(composite, backdrop) >= ratio) return candidate;
  }
  return [toward, toward, toward];
}

export function rgbToHsl([r0, g0, b0]: RGB): string {
  const r = r0 / 255, g = g0 / 255, b = b0 / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  const h = d === 0 ? 0 : max === r ? ((g - b) / d + 6) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return `${(h * 60).toFixed(3)} ${(s * 100).toFixed(3)}% ${(l * 100).toFixed(3)}%`;
}

export function tenantBrandColors(value: unknown) {
  const rgb = parseBrandColor(value);
  if (!rgb) return null;
  return {
    // Check the shared Button's actual 90%-opacity hover; white is the
    // lightest possible backdrop and therefore the worst case for white text.
    action: accessibleColor(rgb, [255, 255, 255], 0, 4.6, .9),
    // Include the shared bg-primary/10 chip treatment, not just plain paper.
    light: accessibleColor(rgb, [242, 242, 241], 0, 4.6, 1, .1),
    dark: accessibleColor(rgb, [38, 38, 38], 255, 4.6, 1, .1),
  };
}

/** Return a cleanup that restores the prior shell rather than leaving the last
 * tenant's inline variables behind on unmount or navigation to the admin app. */
export function applyTenantBranding(root: HTMLElement, primary: unknown): () => void {
  const colors = tenantBrandColors(primary);
  const keys = ['--tenant-primary-light', '--tenant-primary-dark', '--tenant-action'] as const;
  const previous = keys.map(key => root.style.getPropertyValue(key));
  const previousAttribute = root.getAttribute('data-tenant-brand');
  if (colors) {
    for (const [index, color] of [colors.light, colors.dark, colors.action].entries()) root.style.setProperty(keys[index], rgbToHsl(color));
    root.setAttribute('data-tenant-brand', '');
  }
  return () => {
    keys.forEach((key, i) => previous[i] ? root.style.setProperty(key, previous[i]) : root.style.removeProperty(key));
    if (previousAttribute === null) root.removeAttribute('data-tenant-brand');
    else root.setAttribute('data-tenant-brand', previousAttribute);
  };
}
